/**
 * Home Page Widget Registry.
 *
 * Pure WidgetType -> widget mapping for the home PL pipeline. The home page's primary widget is the
 * generic HOMEPAGE_BANNER (HERO_BANNER / HERO_CAROUSEL / MULTIPLE_HERO_BANNERS, keyed off displayType).
 * Shared content/marketing widgets are reused from the same registry CLP/PDP use; home-specific widgets
 * (CATEGORIES_SHOWCASE, FEATURED_PRODUCTS_SHOWCASE, HALF_BANNER_HALF_PRODUCT_TILES) are integrated as they migrate to PL.
 */

import { WidgetType } from '@lastbrand/storefront-proto-registry/api/v1/enums'

import { asBaseWidget, PageWidgetRegistry } from '@src/components/pl-base-widget'
import { CategoriesShowcaseWidget } from '@src/components/widgets/categories-showcase'
import { FeaturedProductsShowcaseWidget } from '@src/components/widgets/featured-products-showcase'
import { HalfBannerHalfProductTilesWidget } from '@src/components/widgets/half-banner-half-product-tiles'
import { HeaderLinkScrollingMenuWidget } from '@src/components/widgets/header-link-scrolling-menu'
import { HomepageBannerWidget } from '@src/components/widgets/homepage-banner'
import { HonestReviewsWidget } from '@src/components/widgets/honest-reviews'
import { SingleReviewCardWidget } from '@src/components/widgets/honest-reviews/single-review-card-widget'
import { InfluencerV2Widget } from '@src/components/widgets/influencer-v2'
import { InfluencerV2CardWidget } from '@src/components/widgets/influencer-v2/influencer-v2-card-widget'
import { PersonalisedProductsWidget } from '@src/components/widgets/personalised-products'
import { RichTextWidget } from '@src/components/widgets/rich-text'
import { SingleProductCardWidget } from '@src/components/widgets/single-product-card'

export function getWidgetRegistry(): PageWidgetRegistry {
  return {
    // Generic homepage banner: one widget renders all three banner section types based on displayType.
    [WidgetType.HOMEPAGE_BANNER]: asBaseWidget(HomepageBannerWidget),
    // Shared content/marketing widgets (self-contained, render from their own WidgetData).
    [WidgetType.RICH_TEXT]: asBaseWidget(RichTextWidget),
    // Homepage-specific widgets.
    // mWeb quick-link scrolling menu — replaces the header's tertiary-menu row on the PL home page
    // (the header stops force-rendering it there; see dynamic-client-root-layout).
    [WidgetType.HEADER_LINK_SCROLLING_MENU]: asBaseWidget(HeaderLinkScrollingMenuWidget),
    [WidgetType.CATEGORIES_SHOWCASE]: asBaseWidget(CategoriesShowcaseWidget),
    // Featured products swimlane (v2 nested): FEATURED_PRODUCTS_SHOWCASE is the container
    // arranging its pre-rendered SINGLE_PRODUCT_CARD children (BackToSchool section visuals).
    // PDP also registers SINGLE_PRODUCT_CARD to the same shared leaf (`widgets/single-product-card`);
    // the page-specific behavior lives in the container widgets (HPL/PGC vs home swimlanes).
    [WidgetType.FEATURED_PRODUCTS_SHOWCASE]: asBaseWidget(FeaturedProductsShowcaseWidget),
    [WidgetType.SINGLE_PRODUCT_CARD]: asBaseWidget(SingleProductCardWidget),
    [WidgetType.HALF_BANNER_HALF_PRODUCT_TILES]: asBaseWidget(HalfBannerHalfProductTilesWidget),
    // Honest reviews (v2 nested): HONEST_REVIEWS is the container arranging its pre-rendered
    // SINGLE_REVIEW_CARD children (HonestReviewsV2 section visuals, DS + legacy branches).
    [WidgetType.HONEST_REVIEWS]: asBaseWidget(HonestReviewsWidget),
    [WidgetType.SINGLE_REVIEW_CARD]: asBaseWidget(SingleReviewCardWidget),
    // Personalised products swimlane (v2 nested): the feed is resolved at PL serve time and
    // arrives as nested SINGLE_PRODUCT_CARD widgets; the container arranges the pre-rendered
    // shared-leaf cards inside the PersonalisedProducts chrome (DS + legacy branches).
    [WidgetType.PERSONALISED_PRODUCTS]: asBaseWidget(PersonalisedProductsWidget),
    // Home IRL (Quince IRL, v2 nested): INFLUENCER_SECTION is the container arranging its
    // pre-rendered INFLUENCER_CARD children (InfluencerV2 section visuals). These are home-specific
    // widgets — the PDP registry maps the same types to to the generic influencer-section widgets (DS-gated) + non-DS card.
    [WidgetType.INFLUENCER_SECTION]: asBaseWidget(InfluencerV2Widget),
    [WidgetType.INFLUENCER_CARD]: asBaseWidget(InfluencerV2CardWidget),
  }
}
